import type { BrowserOptions } from "@sentry/browser";
import * as Sentry from "@sentry/react";
import { getEnvironmentType } from "../utils/utils";
import { ENV_TYPES } from "../types/env.types";

const envType = getEnvironmentType();

let sentryEnv;

switch (envType) {
  case ENV_TYPES.PRODUCTION:
    sentryEnv = "production";
    break;
  case ENV_TYPES.STAGING:
    sentryEnv = "staging";
    break;
  default:
    sentryEnv = "development";
}

export const sentryConfig: BrowserOptions = {
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: sentryEnv,
  integrations: [
    // Browser tracing for performance monitoring (without distributed tracing headers)
    new Sentry.BrowserTracing({
      // Disable distributed tracing to avoid CORS issues with backend
      tracePropagationTargets: [],
    }),
    // Session replay for error debugging
    new Sentry.Replay({
      maskAllText: false,
      blockAllMedia: false,
      // Network details help debug API issues
      networkDetailAllowUrls: [window.location.origin],
    }),
    // Breadcrumbs for user actions
    new Sentry.BrowserProfilingIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: envType === ENV_TYPES.PRODUCTION ? 0.02 : 0.1,
  //Session sample rate removed as it offers no actionable data
  //Session sample rate records full session regardless of errors
  replaysOnErrorSampleRate: envType === ENV_TYPES.PRODUCTION ? 1.0 : 0,

  // Enhanced breadcrumbs
  beforeBreadcrumb(breadcrumb, hint) {
    // Add extra context to console breadcrumbs
    if (breadcrumb.category === "console") {
      return breadcrumb;
    }

    // Add extra context to fetch/XHR breadcrumbs
    if (breadcrumb.category === "fetch" || breadcrumb.category === "xhr") {
      // Add timestamp
      breadcrumb.data = {
        ...breadcrumb.data,
        timestamp: new Date().toISOString(),
      };
    }

    return breadcrumb;
  },

  // Enhanced error event processing
  beforeSend(event, hint) {
    // Add extra context to all events
    if (event.request) {
      event.request.headers = {
        ...event.request.headers,
        "User-Agent": navigator.userAgent,
      };
    }

    // Add viewport info
    event.contexts = {
      ...event.contexts,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
      },
    };

    return event;
  },

  ignoreErrors: [
    "ReferenceError: Can't find variable: fbq",
    // Random plugins/extensions
    "top.GLOBALS",
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    "originalCreateNotification",
    "canvas.contentDocument",
    "MyApp_RemoveAllHighlights",
    "http://tt.epicplay.com",
    "Can't find variable: ZiteReader",
    "jigsaw is not defined",
    "ComboSearch is not defined",
    "http://loading.retry.widdit.com/",
    "atomicFindClose",
    // Facebook borked
    "fb_xd_fragment",
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
    "bmi_SafeAddOnload",
    "EBCallBackMessageReceived",
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    "conduitPage",
    // Generic error code from errors outside the security sandbox
    // You can delete this if using raven.js > 1.0, which ignores these automatically.
    "Script error.",
    "Failed to fetch",
    "TypeError: Cannot read property 'title' of null",
    // Network errors that aren't actionable
    "NetworkError",
    "Network request failed",
    "Load failed",
  ],

  // Ignore certain URLs that cause noise
  denyUrls: [
    // Browser extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^moz-extension:\/\//i,
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
  ],
};
